import React, { ChangeEvent, useEffect, useRef, useState } from "react";
import Accordion from "react-bootstrap/Accordion";
import InputRange from "react-input-range";
import "react-input-range/lib/css/index.css";
import { useGetAllCategoryListQuery } from "../../../utils/api/category/category.api";
import { AlertInfo } from "../../common/Alert";
import { QueryUrl, sortByAlphabetical } from "../../../utils/Utils";
import { Color } from "../../../utils/theme";

const PRIX_PALIERS_DEFAULT = [
  0, 50_000, 100_000, 250_000, 500_000, 1_000_000, 2_000_000, 3_500_000, 5_000_000,
];

function priceToMinIndex(price: number, paliers: number[]): number {
  const maxIdx = paliers.length - 1;
  const i = paliers.findIndex((p) => p >= price);
  return i === -1 ? maxIdx : i;
}

function priceToMaxIndex(price: number, paliers: number[]): number {
  const maxIdx = paliers.length - 1;
  for (let i = maxIdx; i >= 0; i--) {
    if (paliers[i] <= price) return i;
  }
  return 0;
}

function formatPalierLabel(p: number): string {
  if (p >= 1_000_000) return `${p / 1_000_000}M`;
  if (p >= 1_000) return `${p / 1_000}k`;
  return String(p);
}

function FilterItems({
  searchParams,
  setSearchParams,
  query,
  setQuery,
  setPage,
  prixPaliers = PRIX_PALIERS_DEFAULT,
  minAvailablePrice,
  maxAvailablePrice,
}: {
  searchParams: URLSearchParams;
  setSearchParams: (nextInit: string) => void;
  query: any;
  setQuery: (q: any) => void;
  setPage: (page: number) => void;
  prixPaliers?: number[];
  minAvailablePrice?: number | null;
  maxAvailablePrice?: number | null;
}) {
  const paliers = prixPaliers?.length ? prixPaliers : PRIX_PALIERS_DEFAULT;
  const PRIX_SLIDER_MAX_INDEX = paliers.length - 1;

  const [selectedCat, setSelectedCat] = React.useState<any>(
    searchParams?.get("sous_categorie")
      ? JSON.parse(searchParams?.get("sous_categorie") || "[]")
      : []
  );
  const [parent, setParent] = React.useState<any>(
    searchParams?.get("categorie") || ""
  );

  const pmin = searchParams?.get("prix_min");
  const pmax = searchParams?.get("prix_max");
  const minPrice = pmin ? Number(pmin) : 0;
  const maxPrice = pmax ? Number(pmax) : paliers[PRIX_SLIDER_MAX_INDEX];

  const [value, setValue] = React.useState<{ min: number; max: number }>(() => ({
    min: priceToMinIndex(minPrice, paliers),
    max: priceToMaxIndex(maxPrice, paliers),
  }));

  const isBlockedRef = useRef(true);

  useEffect(() => {
    const t = setTimeout(() => {
      isBlockedRef.current = false;
    }, 500);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    if (isBlockedRef.current) return;
    const pmin2 = searchParams?.get("prix_min");
    const pmax2 = searchParams?.get("prix_max");
    const minP = pmin2 ? Number(pmin2) : 0;
    const maxP = pmax2 ? Number(pmax2) : paliers[PRIX_SLIDER_MAX_INDEX];
    setValue({
      min: priceToMinIndex(minP, paliers),
      max: priceToMaxIndex(maxP, paliers),
    });
  }, [searchParams?.get("prix_min"), searchParams?.get("prix_max"), paliers]);

  const {
    data = { results: [] },
    isLoading,
  } = useGetAllCategoryListQuery();

  const [activeKey, setActiveKey] = useState<string | number | null>(null);
  const [accordionActiveKeys, setAccordionActiveKeys] = useState<string[]>([]);
  const [hoverPrice, setHoverPrice] = useState<{ label: string; x: number; index: number } | null>(null);
  const sliderWrapRef = useRef<HTMLDivElement>(null);

  const categorieSlug = searchParams?.get("categorie") || "";
  const categories = sortByAlphabetical(data?.results ?? []);

  useEffect(() => {
    if (!categorieSlug || !categories.length) return;
    const index = categories.findIndex((cat: any) => cat?.slug === categorieSlug);
    if (index === -1) return;
    const category = categories[index];
    setActiveKey(category?.id ?? null);
    setAccordionActiveKeys([String(index)]);
  }, [categorieSlug, data?.results]);

  const getPositionFromEvent = (e: { clientX: number }) => {
    const wrap = sliderWrapRef.current;
    if (!wrap || !paliers.length) return null;
    const rect = wrap.getBoundingClientRect();
    const percent = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
    const index = Math.round(percent * PRIX_SLIDER_MAX_INDEX);
    const clampedIndex = Math.max(0, Math.min(index, PRIX_SLIDER_MAX_INDEX));
    const price = paliers[clampedIndex];
    const label = `${Number(price).toLocaleString("fr-FR")} F CFA`;
    const localX = e.clientX - rect.left;
    return { label, x: localX, index: clampedIndex };
  };

  const handleSliderMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    const pos = getPositionFromEvent(e);
    if (pos) setHoverPrice(pos);
  };

  const handleSliderMouseLeave = () => setHoverPrice(null);

  const handleSliderClick = (e: React.MouseEvent<HTMLDivElement>) => {
    const pos = getPositionFromEvent(e);
    if (!pos || isBlockedRef.current) return;
    const idx = pos.index;
    if (idx <= value.min) setValue((v) => ({ ...v, min: Math.min(idx, v.max) }));
    else if (idx >= value.max) setValue((v) => ({ ...v, max: Math.max(idx, v.min) }));
    else if (idx - value.min <= value.max - idx) setValue((v) => ({ ...v, min: Math.min(idx, v.max) }));
    else setValue((v) => ({ ...v, max: Math.max(idx, v.min) }));
  };

  const handleToggle = (key) => {
    setActiveKey(key);
  };

  useEffect(() => {
    const params = QueryUrl("", query);
    setSearchParams(params);
  }, [query]);

  useEffect(() => {
    let queryCopy = { ...query };
    queryCopy = {
      ...queryCopy,
      sous_categorie: selectedCat?.length ? JSON.stringify(selectedCat) : "",
    };
    setQuery(queryCopy);
  }, [selectedCat]);

  const handleFilter = () => {
    if (isBlockedRef.current) return;
    // Ne pas envoyer de filtre prix si la plage est complète (évite d'exclure
    // les produits sans prix / hors paliers)
    const isFullRange =
      value.min === 0 && value.max === PRIX_SLIDER_MAX_INDEX;
    const queryCopy = {
      ...query,
      prix_min: isFullRange ? "" : String(paliers[value.min]),
      prix_max: isFullRange ? "" : String(paliers[value.max]),
    };
    setPage(1);
    setQuery(queryCopy);
  };

  useEffect(() => {
    handleFilter();
  }, [value.min, value.max]);

  const handleChangeParent = (item) => {
    setParent(item?.slug);
    let queryCopy = { ...query };
    queryCopy = {
      ...queryCopy,
      categorie: item?.slug,
      sous_categorie: [],
    };
    setSelectedCat([]);
    setQuery(queryCopy);
  };

  const handleSelectedCat = (e: ChangeEvent<HTMLInputElement>, item) => {
    let selectedCopy = [...selectedCat];
    if (item?.slug !== parent) {
      handleChangeParent(item);
      selectedCopy = [];
    }

    if (e?.target?.checked) {
      selectedCopy?.push(e?.target?.value);
    } else {
      selectedCopy = selectedCopy?.filter((el) => el !== e?.target?.value);
    }
    setSelectedCat(selectedCopy);
    setPage(1);
  };

  const handleClick = (item) => {
    handleToggle(item?.id);
    setParent(item?.slug);
    let queryCopy = { ...query };
    queryCopy = {
      ...queryCopy,
      categorie: item?.slug,
      sous_categorie: [],
    };
    setSelectedCat([]);
    setQuery(queryCopy);
    setPage(1);
  };

  return (
    <div className="col-lg-3 col-md-5 col-left-achat-page">
      <div className="content-col-left-achat-page">
        <div className="content-others-value my-4 pb-3">
          <p className="content-title-section-left pb-2">Catégories</p>

          <div className="categories_and_subcategories">
            {!!isLoading && <p>Loading...</p>}

            <Accordion
              className="category-filter-acordion"
              id="accordionCategories"
              alwaysOpen
              activeKey={accordionActiveKeys}
              onSelect={(k) => setAccordionActiveKeys(k != null ? (Array.isArray(k) ? k : [String(k)]) : [])}
            >
              {!isLoading &&
                (sortByAlphabetical(data?.results)?.length ? (
                  sortByAlphabetical(data?.results)?.map((category, i) => (
                    <Accordion.Item
                      eventKey={`${i}`}
                      key={category?.id}
                      className="category-filter-acordion-item mb-1"
                    >
                      <Accordion.Header
                        className="mb-0"
                        onClick={() => handleClick(category)}
                      >
                        <span
                          className="category_parent_name"
                          style={{
                            color:
                              category?.id == activeKey
                                ? Color.themeColor
                                : "#000000b3",
                          }}
                        >
                          {category?.nom}
                        </span>
                      </Accordion.Header>
                      {category?.sous_categorie?.length ? (
                        <Accordion.Body>
                          {sortByAlphabetical(category?.sous_categorie)?.map(
                            (sub) => (
                              <div
                                className="content-col-form-register-tabs cpntrol mb-1"
                                key={sub?.id}
                              >
                                <label className="label-checkbox-register-tabs category_child_name">
                                  <input
                                    type="checkbox"
                                    className="input-checkbox-register-tabs me-2"
                                    checked={
                                      selectedCat &&
                                      selectedCat?.length &&
                                      selectedCat?.includes(sub?.slug)
                                    }
                                    value={sub?.slug}
                                    onChange={(e) =>
                                      handleSelectedCat(e, category)
                                    }
                                  />
                                  {sub?.nom}
                                </label>
                              </div>
                            ),
                          )}
                        </Accordion.Body>
                      ) : null}
                    </Accordion.Item>
                  ))
                ) : (
                  <AlertInfo message="Aucune catégorie disponible pour le moment" />
                ))}
            </Accordion>
          </div>
        </div>
        <p className="content-title-section-left">
          Prix <span style={{ fontWeight: 300, fontSize: 14 }}>(F CFA)</span>
        </p>
        <div className="filter-prix-slider pb-4 py-4">
          <div
            ref={sliderWrapRef}
            className="filter-prix-slider-wrap"
            onMouseMove={handleSliderMouseMove}
            onMouseLeave={handleSliderMouseLeave}
            onClick={handleSliderClick}
          >
            {hoverPrice && (
              <div
                className="filter-prix-hover-tooltip"
                style={{ left: hoverPrice.x }}
              >
                {hoverPrice.label}
              </div>
            )}
            <InputRange
              minValue={0}
              maxValue={PRIX_SLIDER_MAX_INDEX}
              value={value}
              onChange={(v) => setValue(v as { min: number; max: number })}
              formatLabel={() => ""}
            />
          </div>
          <div className="filter-prix-extremes">
            <span>
              {formatPalierLabel(
                minAvailablePrice != null && Number.isFinite(minAvailablePrice)
                  ? Math.max(minAvailablePrice, paliers[0])
                  : paliers[0]
              )}{" "}
              F
            </span>
            <span>
              {formatPalierLabel(
                maxAvailablePrice != null && Number.isFinite(maxAvailablePrice)
                  ? Math.min(maxAvailablePrice, paliers[PRIX_SLIDER_MAX_INDEX])
                  : paliers[PRIX_SLIDER_MAX_INDEX]
              )}{" "}
              F
            </span>
          </div>
          <p className="filter-prix-slider-range mt-2 mb-0 small text-muted text-center">
            {formatPalierLabel(paliers[value.min])} –{" "}
            {formatPalierLabel(paliers[value.max])} F CFA
          </p>
        </div>
      </div>
    </div>
  );
}

export default FilterItems;
